// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { OperationRequestOptions } from '@azure/core-client';
import { GithubInstallExchangeOptions, HttpHeader, isAbsoluteUrl, isNonEmptyString } from '@microsoft/vscodeedu-api';
import { getRouteUrl, trackInfo, trackWarning } from '../utilities';
import { PersistedString } from '../utilities/persisted-value';
import { anonymousClient } from './config-context';
import { Redirecting, VSCodeEduIdentityProvider } from './vsce-identity-provider';

const prefix = 'GithubLinkHandler:';
const redirectRouteKey = `${prefix}redirectRoute`;
const affinityRegionKey = `${prefix}affinityRegion`;

/**
 * Handles GitHub OAuth flow for linking GitHub account.
 */
export class GithubLinkHandler {
    private trace = (message: string) => trackInfo(`${prefix} ${message}`);
    private warning = (message: string) => trackWarning(`${prefix} ${message}`);

    private readonly identityProvider: VSCodeEduIdentityProvider;
    private readonly redirectRoute = new PersistedString(redirectRouteKey);
    private readonly affinityRegion = new PersistedString(affinityRegionKey);

    constructor() {
        this.identityProvider = new VSCodeEduIdentityProvider();
    }

    public async linkGithubAccount(redirectTo?: string): Promise<never> {
        if (redirectTo === undefined || isAbsoluteUrl(redirectTo)) {
            this.redirectRoute.set(redirectTo);
        } else {
            this.redirectRoute.set(new URL(getRouteUrl(redirectTo), window.location.origin).toString());
        }

        this.trace(`startLink: Creating a new GitHub OAuth flow with redirect to ${this.redirectRoute}`);
        this.redirect(await this.startLink());
    }

    // Redirects to specified URL and throws Redirecting error.
    private redirect(url: string, replace?: boolean): never {
        this.trace(`Redirecting to url: ${url} replace: ${replace}`);
        if (replace === true) {
            window.location.replace(url);
        } else {
            window.location.assign(url);
        }
        throw new Redirecting();
    }

    private async startLink() {
        this.trace('startLink: Starting GitHub OAuth flow');
        let result: string;
        const requestOptions = await this.getRequestOptions();

        const root = window.location.origin;
        const redirectUrl = new URL(getRouteUrl('/github-redirect'), root).toString();

        await anonymousClient.githubInstallStart(redirectUrl, {
            onResponse: ({ status, bodyAsText, headers }) => {
                if (status === 200 && isNonEmptyString(bodyAsText)) {
                    result = bodyAsText;
                    this.affinityRegion.set(headers.get(HttpHeader.XMSAffinityRegion));
                } else {
                    this.warning(`Failed to obtain github install URL: ${status}.`);
                    throw new Error(`Failed to obtain github install URL: ${status}.`);
                }
            },
            requestOptions,
        });
        return result!;
    }

    public async handleGithubLinkRedirect(): Promise<void> {
        if (window.location.pathname !== getRouteUrl('github-redirect')) {
            return;
        }

        this.trace('handleRedirect: Handling GitHub OAuth redirect');

        const params = new URLSearchParams(window.location.search);

        const code = params.get('code');
        if (code === null) {
            this.warning('handleRedirect: Missing code parameter in URL.');
            return;
        }

        const state = params.get('state');
        if (!isNonEmptyString(state)) {
            this.warning("handleEntraRedirect: Missing 'state' query parameter.");
            return;
        }

        this.trace('handleRedirect: Completing GitHub OAuth flow');
        await this.completeGithubLink(code, state);

        const redirectTo = this.redirectRoute.get();
        if (isNonEmptyString(redirectTo)) {
            this.trace(`handleGithubLinkRedirect: Redirecting to ${redirectTo}`);
            this.redirectRoute.clear();
            this.affinityRegion.clear();
            this.redirect(redirectTo, true);
        }
    }

    private async completeGithubLink(code: string, state: string): Promise<void> {
        this.trace('completeGithubLink: Completing GitHub OAuth flow');
        const options: GithubInstallExchangeOptions = { accessCode: code, state };

        const requestOptions = await this.getRequestOptions();
        const affinityRegion = this.affinityRegion.get();
        if (affinityRegion !== undefined) {
            requestOptions.customHeaders![HttpHeader.XMSAffinityRegion] = affinityRegion;
        }

        await anonymousClient.githubInstallExchange(options, { requestOptions });
    }

    private async getRequestOptions(): Promise<OperationRequestOptions> {
        return {
            customHeaders: {
                [HttpHeader.Authorization]: `Bearer ${await this.identityProvider.getAccessToken()}`,
            },
        };
    }
}
